import { useState } from "react";
import { toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAcknowledgeAlarm } from "@/features/alarms/hooks";
import { occurrenceTitle } from "@/features/attention/occurrence-meta";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import type { AttentionOccurrence } from "@/types/attention.types";

interface Props {
  occurrence: AttentionOccurrence | null;
  open: boolean;
  onClose: () => void;
}

export function AckDialog({ occurrence, open, onClose }: Props) {
  const [cause, setCause] = useState("");
  const ack = useAcknowledgeAlarm();

  if (!open || !occurrence || !occurrence.alarm) return null;
  const alarmRefId = occurrence.alarm.refId;

  async function submit() {
    if (cause.trim().length === 0) {
      toast.error("Causa é obrigatória.");
      return;
    }
    try {
      await ack.mutateAsync({ id: alarmRefId, data: { cause: cause.trim() } });
      toast.success("Alarme reconhecido.");
      setCause("");
      onClose();
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao reconhecer.", { description: msg });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-background/80 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-md animate-scale-in">
        <CardContent className="space-y-3 pt-6">
          <div>
            <h2 className="text-base font-semibold">Reconhecer alarme</h2>
            <p className="text-xs text-muted-foreground">{occurrenceTitle(occurrence)}</p>
          </div>
          <div className="rounded-md border border-info/30 bg-info/5 p-2 text-xs">
            <strong>Ação esperada:</strong> {occurrence.action}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cause">Causa (obrigatório)</Label>
            <Input
              id="cause"
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              placeholder="Ex: Falha conhecida no PLC, intervenção em andamento."
              maxLength={500}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => void submit()} disabled={ack.isPending}>
              {ack.isPending ? "Reconhecendo…" : "Reconhecer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
